import React, { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useSignIn, useEvalToken, useWhoami } from '../../hooks/auth/useAuthClient';
import {
  initializeAuth,
  checkEmailVerification,
  signInToBackend,
  fetchWhoami,
  setShowEmailVerification,
  setShowPersonnelNotFound,
  setPendingAccessToken,
  setBackendSignInError,
  setLoading,
  clearAuth,
  setIsAuthenticated,
  setAuth0User,
  setAuth0Error,
} from '../../store/slices/authSlice';
import { storage } from '../../utils/storage';
import { auth0Config } from '../../config/auth0Config';
import {
  normalizeError,
  logError,
  isTokenError,
  isPersonnelError,
} from '../../utils/errorHandler';

function AuthManager({ children }) {
  const dispatch = useAppDispatch();
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    getAccessTokenSilently,
    logout: auth0Logout,
    isLoading: auth0Loading,
    error: auth0Error,
  } = useAuth0();

  const authState = useAppSelector((state) => state.auth);
  const {
    showEmailVerification,
    showPersonnelNotFound,
    isAuthenticated,
    isLoading,
  } = authState;

  const signIn = useSignIn();
  const evalToken = useEvalToken();
  const whoami = useWhoami();
  const whoamiRef = useRef(whoami);

  useEffect(() => {
    whoamiRef.current = whoami;
  }, [whoami]);

  const backendSignInAttemptedRef = useRef(false);
  const isProcessingRef = useRef(false);
  const evalTokenCalledRef = useRef(false);
  const isSigningOutRef = useRef(false);
  const whoamiFetchedRef = useRef(false);

  useEffect(() => {
    dispatch(initializeAuth());
    const storedError = sessionStorage.getItem('signin_error');
    if (storedError) {
      dispatch(setBackendSignInError(storedError));
      sessionStorage.removeItem('signin_error');
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(setAuth0User(auth0User));
  }, [auth0User, dispatch]);

  useEffect(() => {
    dispatch(setAuth0Error(auth0Error));
  }, [auth0Error, dispatch]);

  useEffect(() => {
    const handleAuth0SignIn = async () => {
      if (
        backendSignInAttemptedRef.current ||
        isProcessingRef.current ||
        isSigningOutRef.current
      ) {
        return;
      }

      if (showEmailVerification || showPersonnelNotFound) {
        return;
      }

      if (isAuthenticated && authState.user && auth0IsAuthenticated) {
        if (!whoamiFetchedRef.current) {
          whoamiFetchedRef.current = true;

          if (authState.showEmailVerification === null) {
            dispatch(setShowEmailVerification(false));
          }
          if (authState.showPersonnelNotFound === null) {
            dispatch(setShowPersonnelNotFound(false));
          }

          try {
            await dispatch(fetchWhoami({ whoami: whoamiRef.current })).unwrap();
          } catch (whoamiError) {
          }

          dispatch(setLoading(false));
        } else if (isLoading) {
          dispatch(setLoading(false));
        }
        return;
      }

      if (
        auth0IsAuthenticated &&
        auth0User &&
        !isLoading &&
        !auth0Loading &&
        !evalTokenCalledRef.current
      ) {
        backendSignInAttemptedRef.current = true;
        isProcessingRef.current = true;

        try {
          dispatch(setLoading(true));
          dispatch(setBackendSignInError(null));
          let accessToken;
          try {
            accessToken = await getAccessTokenSilently({
              audience: auth0Config.authorizationParams.audience,
              scope: 'openid profile email offline_access',
              detailedResponse: false,
            });
          } catch (tokenError) {
            const normalizedError = normalizeError(tokenError);
            logError(tokenError, 'AuthManager - getAccessToken');

            if (normalizedError.isTokenError || isTokenError(tokenError)) {
              backendSignInAttemptedRef.current = false;
              dispatch(setIsAuthenticated(false));
              dispatch(clearAuth());
              storage.removeAuth();
              dispatch(setBackendSignInError(normalizedError.message));
              dispatch(setLoading(false));
              return;
            }

            throw new Error(normalizedError.message || 'Unable to get access token. Please try logging in again.');
          }

          if (!accessToken) {
            throw new Error('Access token is empty');
          }

          evalTokenCalledRef.current = true;
          try {
            const emailVerified = await dispatch(
              checkEmailVerification({ accessToken, evalToken })
            ).unwrap();

            if (!emailVerified) {
              dispatch(clearAuth());
              storage.removeAuth();
              dispatch(setPendingAccessToken(accessToken));
              dispatch(setShowEmailVerification(true));
              backendSignInAttemptedRef.current = false;
              isProcessingRef.current = false;
              evalTokenCalledRef.current = false;
              dispatch(setLoading(false));
              return;
            }
          } catch (error) {
            logError(error, 'AuthManager - checkEmailVerification');

            if (isPersonnelError(error)) {
              backendSignInAttemptedRef.current = false;
              isProcessingRef.current = false;
              evalTokenCalledRef.current = false;
              dispatch(setLoading(false));
              return;
            }
          }

          if (isAuthenticated) {
            if (authState.showEmailVerification === null) {
              dispatch(setShowEmailVerification(false));
            }
            if (authState.showPersonnelNotFound === null) {
              dispatch(setShowPersonnelNotFound(false));
            }
            dispatch(setLoading(false));
            isProcessingRef.current = false;
            backendSignInAttemptedRef.current = false;
            evalTokenCalledRef.current = true;
            return;
          }

          await dispatch(
            signInToBackend({ accessToken, signIn, auth0User })
          ).unwrap();

          if (!whoamiFetchedRef.current) {
            whoamiFetchedRef.current = true;
            try {
              await dispatch(fetchWhoami({ whoami: whoamiRef.current })).unwrap();
            } catch (whoamiError) {
            }
          }
        } catch (error) {
          const normalizedError = normalizeError(error);
          logError(error, 'AuthManager - signInToBackend');

          if (!normalizedError.isPersonnelError && !isPersonnelError(error)) {
            sessionStorage.setItem('signin_error', normalizedError.message);

            if (auth0IsAuthenticated && auth0Logout) {
              try {
                await auth0Logout({
                  logoutParams: {
                    returnTo: window.location.origin
                  }
                });
              } catch (logoutError) {
                logError(logoutError, 'AuthManager - Auth0 Logout');
              }
            }
          }

          backendSignInAttemptedRef.current = false;
          isProcessingRef.current = false;
        } finally {
          dispatch(setLoading(false));
          isProcessingRef.current = false;
        }
      }
    };

    if (!auth0Loading && (!isLoading || isAuthenticated)) {
      handleAuth0SignIn();
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    isAuthenticated,
    auth0Loading,
    isLoading,
    showEmailVerification,
    showPersonnelNotFound,
    dispatch,
    evalToken,
    signIn,
    authState.user,
    authState.showEmailVerification,
    authState.showPersonnelNotFound,
    getAccessTokenSilently,
    auth0Logout,
  ]);

  useEffect(() => {
    if (!auth0IsAuthenticated) {
      const wasSigningOut = isSigningOutRef.current;

      backendSignInAttemptedRef.current = false;
      isProcessingRef.current = false;
      evalTokenCalledRef.current = false;
      isSigningOutRef.current = false;
      whoamiFetchedRef.current = false;

      dispatch(setShowPersonnelNotFound(false));
      if (!wasSigningOut) {
        dispatch(setShowEmailVerification(false));
      }
    }
  }, [auth0IsAuthenticated, dispatch]);

  useEffect(() => {
    const handleUnauthorized = (event) => {
      console.warn('Unauthorized event detected:', event.detail);

      if (auth0IsAuthenticated && auth0Logout && !isSigningOutRef.current) {
        isSigningOutRef.current = true;
        console.log('Session expired - logging out...');

        storage.clear();
        dispatch(clearAuth());

        auth0Logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [auth0IsAuthenticated, auth0Logout, dispatch]);

  return <>{children}</>;
}

export default AuthManager;
